import { Alert, Anchor, Button, Stack, Text, TextInput } from '@mantine/core';
import { IconAlertCircle, IconCheck, IconMail } from '@tabler/icons-react';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { toUserMessage } from '@/lib/api';
import { resendVerification, verifyEmail } from './api';
import { AuthLayout } from './AuthLayout';

/**
 * Подтверждение почты по ссылке из письма (§14).
 *
 * Устроено как страница смены пароля и по тем же причинам: токен приходит
 * фрагментом адреса — всё, что до решётки, попадает в журнал веб-сервера, а
 * фрагмент браузер серверу не отправляет вовсе. Прочитав токен, страница сразу
 * стирает его из адресной строки, чтобы он не остался в истории и в ссылке,
 * которую человек может кому-то переслать.
 *
 * Подтверждение происходит само, без кнопки: человек уже сделал выбор, нажав
 * ссылку в письме. Просить его нажать ещё раз — заставлять подтверждать
 * подтверждение.
 */

function takeTokenFromUrl(): string {
  const raw = window.location.hash.startsWith('#') ? window.location.hash.slice(1) : '';
  const token = new URLSearchParams(raw).get('token') ?? '';

  if (token) {
    window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}`);
  }
  return token;
}

export function VerifyEmailPage() {
  const [token] = useState(takeTokenFromUrl);

  const confirm = useMutation({ mutationFn: () => verifyEmail({ token }) });
  const resend = useMutation({ mutationFn: (email: string) => resendVerification({ email }) });
  const [email, setEmail] = useState('');

  /*
    Ровно один запрос на одно открытие страницы.

    Ссылка одноразовая: второй запрос с тем же ключом получает законный отказ —
    и человек видит ошибку на месте успеха, ничего не сделав дважды. А
    отправить дважды легко: React в отладочной сборке намеренно прогоняет
    эффекты по два раза, и первый же прогон гасил ключ для второго. Именно так
    это и выглядело, пока сторож не появился.

    Сторож — ref, а не состояние: он не должен вызывать перерисовку и должен
    пережить повторный вызов эффекта на том же экземпляре.
  */
  const requested = useRef(false);
  useEffect(() => {
    if (!token || requested.current) return;
    requested.current = true;
    confirm.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  if (confirm.isSuccess) {
    return (
      <AuthLayout title="Почта подтверждена" subtitle="Теперь можно войти.">
        <Stack gap="md">
          <Alert color="teal" icon={<IconCheck size={17} />} title="Готово">
            Адрес {confirm.data.email} подтверждён.
          </Alert>
          <Button component={Link} to="/login">
            Войти
          </Button>
        </Stack>
      </AuthLayout>
    );
  }

  const failed = !token || confirm.isError;

  return (
    <AuthLayout
      title={failed ? 'Ссылка не сработала' : 'Подтверждаем почту'}
      subtitle={failed ? 'Такое бывает, если письмо пролежало слишком долго.' : 'Одну секунду.'}
    >
      <Stack gap="md">
        {failed ? (
          <>
            <Alert color="red" icon={<IconAlertCircle size={17} />} title="Не подтвердилось">
              {token
                ? toUserMessage(confirm.error)
                : 'В адресе нет ключа — похоже, ссылка скопировалась не целиком.'}
            </Alert>

            {resend.isSuccess ? (
              <Alert color="teal" icon={<IconMail size={17} />} title="Письмо отправлено">
                Если такой адрес зарегистрирован, письмо уже в пути. Загляните в
                почту — и не забудьте про папку со спамом.
              </Alert>
            ) : (
              <>
                <Text size="sm" c="var(--text-muted)">
                  Укажите почту, на которую регистрировались, и мы отправим письмо заново.
                </Text>
                <TextInput
                  label="Email"
                  placeholder="you@example.com"
                  type="email"
                  value={email}
                  onChange={(event) => setEmail(event.currentTarget.value)}
                />
                <Button
                  loading={resend.isPending}
                  disabled={!email.includes('@')}
                  onClick={() => resend.mutate(email.trim())}
                >
                  Отправить письмо заново
                </Button>
              </>
            )}
          </>
        ) : (
          <Text size="sm" c="var(--text-muted)">
            Проверяем ссылку...
          </Text>
        )}

        <Text size="sm" c="var(--text-muted)" ta="center">
          <Anchor component={Link} to="/login">
            Вернуться ко входу
          </Anchor>
        </Text>
      </Stack>
    </AuthLayout>
  );
}
